import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  ResearchPerfEvalCriteria,
  ResearchPerfEvalCriteriaData,
  ResearchPerfEvalCriteriaDataItemRequest,
  ResearchPerfEvalItemOption,
  ResearchPerfEvalReport,
  ResearchPerfEvalReportAuthor,
} from '@/module/ResearchPerfEval/model/ResearchPerfEvalTypes';

import { Id, getQueryKey } from '@feature/utils';

import * as api from './ResearchPerfEvalApi';

const researchPerfEvalKey = getQueryKey('ResearchPerfEval');

export function getResearchPerfEvalsOptions(): UseSuspenseQueryOptions<ResearchPerfEvalReport[]> {
  return {
    queryFn: () => api.getAll(),
    queryKey: researchPerfEvalKey.list(),
  };
}

export function getResearchPerfEvalOptions(evaluationId: Id): UseSuspenseQueryOptions<ResearchPerfEvalReport> {
  return {
    queryFn: () => api.get(evaluationId),
    queryKey: researchPerfEvalKey.one(evaluationId),
  };
}

export function getAuthorResearchPerfEvalOptions(
  evaluationId: Id,
  authorId: Id,
): UseSuspenseQueryOptions<ResearchPerfEvalReportAuthor | null> {
  return {
    queryFn: () => api.getForAuthor(evaluationId, authorId),
    queryKey: researchPerfEvalKey.as([evaluationId, 'author', authorId]),
  };
}

export function getResearchPerfEvalCriteriaOptions(
  evaluationId: Id,
): UseSuspenseQueryOptions<ResearchPerfEvalCriteria[]> {
  return {
    queryFn: () => api.getCriteria(evaluationId),
    queryKey: researchPerfEvalKey.as([evaluationId, 'criteria']),
    staleTime: 5000, // wait 5 seconds before considering the request stale (this deduplicates all the loader provisioning done by the router)
  };
}

export function getResearchPerfEvalCriteriaDataOptions(
  evaluationId: Id,
  authorId: Id,
  orderNo: number,
): UseSuspenseQueryOptions<ResearchPerfEvalCriteriaData> {
  return {
    queryFn: () => api.getCriteriaData(evaluationId, authorId, orderNo),
    queryKey: researchPerfEvalKey.as([evaluationId, 'author', authorId, 'data', orderNo]),
  };
}

export function getResearchPerfEvalCriteriaPossibleItemsOptions<T>(
  evaluationId: Id,
  authorId: Id,
  orderNo: number,
): UseSuspenseQueryOptions<ResearchPerfEvalItemOption<T>[]> {
  return {
    queryFn: () => api.getPossibleItemOptions<T>(evaluationId, authorId, orderNo),
    queryKey: researchPerfEvalKey.as([evaluationId, 'author', authorId, 'data', orderNo, 'possible-options']),
  };
}

export function useResearchPerfEvalOpen(year: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.open(year),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchPerfEvalKey.root(),
      }),
  });
}

export function useResearchPerfEvalStart(evaluationId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.start(evaluationId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchPerfEvalKey.root(),
      }),
  });
}

export function useResearchPerfEvalAddCriteriaItem(evaluationId: Id, authorId: Id, orderNo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ResearchPerfEvalCriteriaDataItemRequest) =>
      api.addCriteriaItem(evaluationId, authorId, orderNo, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchPerfEvalKey.root(),
      }),
  });
}

export function useResearchPerfEvalEditCriteriaItem(evaluationId: Id, authorId: Id, orderNo: number, itemId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ResearchPerfEvalCriteriaDataItemRequest) =>
      api.editCriteriaItem(evaluationId, authorId, orderNo, itemId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchPerfEvalKey.root(),
      }),
  });
}

export function useResearchPerfEvalDeleteCriteriaItem(evaluationId: Id, authorId: Id, orderNo: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: Id) => api.deleteCriteriaItem(evaluationId, authorId, orderNo, itemId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: researchPerfEvalKey.root(),
      }),
  });
}
